import React, { useState, useEffect } from "react";
import { Shield, Network, Database, Cpu, ChevronRight } from "lucide-react";

export default function IntroScene({ onStart }: any) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={styles.container}>
      <iframe 
        src="/assets/card_encryption.html" 
        title="Cipher Dissolution Art"
        style={styles.bgIframe}
      />

      {/* Top Section: Branding & Status */}
      <div style={{
        ...styles.topSection,
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "center" : "flex-start",
        padding: isMobile ? "20px" : "40px"
      }}>
        <div style={styles.topLeft}>
          <img 
            src="/assets/PayLens.png" 
            alt="PayLens" 
            style={{
              ...styles.logo,
              height: isMobile ? 50 : 80
            }} 
          />
        </div>

        <div style={{
          ...styles.topRight,
          position: isMobile ? "static" : "absolute",
          textAlign: isMobile ? "center" : "right",
          marginTop: isMobile ? 12 : 0
        }}>
          <div style={styles.hudLabel}>MISSION_OBJECTIVE</div>
          <h1 style={{
            ...styles.title,
            fontSize: isMobile ? 18 : 24
          }}>ANATOMY OF A TRANSACTION</h1>
          <div style={{
            ...styles.statusLine,
            justifyContent: isMobile ? "center" : "flex-end"
          }}>
            <span style={styles.pulse} />
            <span style={styles.tagline}>SYSTEM READY // WAITING FOR INPUT</span>
          </div>
        </div>
      </div>

      {/* Bottom Section: Protocols & Action */}
      <div style={{
        ...styles.bottomSection,
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "center" : "flex-end",
        padding: isMobile ? "20px" : "40px",
        gap: isMobile ? 24 : 0
      }}>
        <div style={{
          ...styles.bottomLeft,
          position: isMobile ? "static" : "absolute",
          alignItems: isMobile ? "center" : "flex-start"
        }}>
          <div style={styles.hudLabel}>PROTOCOL_STACKS</div>
          <div style={{
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: isMobile ? 8 : 12
          }}>
            <HUDStep icon={<Cpu size={14} />} label="EMV" mobile={isMobile} />
            <HUDStep icon={<Database size={14} />} label="ISO_8583" mobile={isMobile} />
            <HUDStep icon={<Shield size={14} />} label="RSA" mobile={isMobile} />
            <HUDStep icon={<Network size={14} />} label="ROUTING" mobile={isMobile} />
          </div>
        </div>

        <div style={{
          ...styles.bottomRight,
          position: isMobile ? "static" : "absolute"
        }}>
          <button style={{
            ...styles.launchButton,
            width: isMobile ? "240px" : "auto",
            padding: isMobile ? "12px 24px" : "16px 32px"
          }} onClick={onStart}>
            <div style={styles.launchContent}>
              <span style={{
                ...styles.launchLabel,
                fontSize: isMobile ? 14 : 16
              }}>INITIALIZE PIPELINE</span>
              <ChevronRight size={isMobile ? 18 : 20} />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

function HUDStep({ icon, label, mobile }: { icon: any, label: string, mobile: boolean }) {
  return (
    <div style={{
      ...styles.hudStep,
      width: mobile ? "auto" : 200,
      padding: mobile ? "6px 12px" : "8px 16px"
    }}>
      <div style={styles.iconBox}>{icon}</div>
      <span style={{
        ...styles.hudStepLabel,
        fontSize: mobile ? 10 : 12
      }}>{label}</span>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    height: "100vh",
    position: "fixed",
    top: 0,
    left: 0,
    background: "#000",
    color: "#FFF",
    fontFamily: "'Share Tech Mono', monospace",
    overflow: "hidden"
  },
  bgIframe: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
    zIndex: 0,
    opacity: 0.7
  },
  topSection: {
    display: "flex",
    width: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 10,
    boxSizing: "border-box"
  },
  topLeft: {
    flex: "0 0 auto"
  },
  logo: {
    filter: "drop-shadow(0 0 15px rgba(56, 189, 248, 0.4))"
  },
  topRight: {
    right: 40,
    zIndex: 10
  },
  hudLabel: {
    fontSize: 10,
    color: "#38BDF8",
    letterSpacing: 2,
    marginBottom: 8,
    opacity: 0.8
  },
  title: {
    fontWeight: 700,
    letterSpacing: 1,
    margin: 0,
    color: "#E2E8F0"
  },
  statusLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 4
  },
  tagline: {
    fontSize: 11,
    color: "#64748B",
    letterSpacing: 1
  },
  pulse: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#10B981",
    boxShadow: "0 0 8px #10B981"
  },
  bottomSection: {
    display: "flex",
    width: "100%",
    position: "absolute",
    bottom: 0,
    left: 0,
    zIndex: 10,
    boxSizing: "border-box"
  },
  bottomLeft: {
    left: 40,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    zIndex: 10
  },
  hudStep: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "rgba(255, 255, 255, 0.03)",
    borderRadius: 4,
    borderLeft: "2px solid #38BDF8"
  },
  iconBox: {
    color: "#38BDF8"
  },
  hudStepLabel: {
    letterSpacing: 1,
    color: "#94A3B8"
  },
  bottomRight: {
    right: 40,
    zIndex: 10
  },
  launchButton: {
    background: "transparent",
    border: "1px solid #38BDF8",
    color: "#38BDF8",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "inset 0 0 10px rgba(56, 189, 248, 0.1)"
  },
  launchContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16
  },
  launchLabel: {
    fontWeight: 600,
    letterSpacing: 2
  }
};







